import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';
import styles from './ThinkingTrace.module.css';

/** Placeholder lines shown between send and the first token. Fixed height: no layout jump. */
export const ReplySkeleton: React.FC = () => (
  <div className={styles.skeleton} aria-hidden>
    <span className={styles.skLine} style={{ inlineSize: '88%' }} />
    <span className={styles.skLine} style={{ inlineSize: '72%' }} />
    <span className={styles.skLine} style={{ inlineSize: '40%' }} />
  </div>
);

/** True once `active` has been continuously true for `ms` (default 6s time-to-first-token budget). */
export function useSlowAfter(active: boolean, ms = 6000): boolean {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!active) return undefined;
    const id = window.setTimeout(() => setSlow(true), ms);
    return () => window.clearTimeout(id);
  }, [active, ms]);
  return slow;
}

export type StreamNoticeKind = 'slow' | 'stopped' | 'error';

/**
 * Quiet one-line notices under a reply: "Still working…", "Stopped",
 * or an error, with Retry where it makes sense. role=status/alert so each
 * is announced once.
 */
export const StreamNotice: React.FC<{
  kind: StreamNoticeKind;
  message?: string;
  onRetry?: () => void;
}> = ({ kind, message, onRetry }) => {
  const { t } = useTranslation();
  const text =
    message ??
    (kind === 'slow'
      ? t('tutor.slow', 'Still working… this one is taking a little longer.')
      : kind === 'stopped'
        ? t('tutor.stoppedNotice', 'Stopped. You can ask again or edit your question.')
        : t('tutor.streamError', 'The reply was interrupted.'));
  return (
    <div
      className={styles.notice}
      data-kind={kind}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      <span>{text}</span>
      {onRetry && kind !== 'slow' && (
        <button type="button" className={styles.retry} onClick={onRetry}>
          <RotateCcw size={12} aria-hidden />
          {t('tutor.retry', 'Try again')}
        </button>
      )}
    </div>
  );
};
